import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Shield, Clock, Check, AlertCircle, Filter } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ['All', 'Pending', 'Completed', 'Expired'];

export default function BackgroundChecks() {
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchVolunteers();
  }, [statusFilter]);

  const fetchVolunteers = async () => {
    try {
      let url = `${API}/background-checks`;
      if (statusFilter && statusFilter !== 'All') {
        url += `?status=${statusFilter}`;
      }

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVolunteers(data);
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateBackgroundCheck = async (volunteerId, status) => {
    try {
      const response = await fetch(`${API}/volunteers/${volunteerId}/background-check`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Background check status updated');
        fetchVolunteers();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating background check:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending': { class: 'bg-amber-100 text-amber-800', icon: Clock },
      'Completed': { class: 'bg-emerald-100 text-emerald-800', icon: Check },
      'Expired': { class: 'bg-red-100 text-red-800', icon: AlertCircle }
    };
    const config = statusConfig[status] || statusConfig['Pending'];
    const Icon = config.icon;
    
    return (
      <Badge variant="secondary" className={`${config.class} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  const stats = {
    total: volunteers.length,
    pending: volunteers.filter(v => v.background_check_status === 'Pending').length,
    completed: volunteers.filter(v => v.background_check_status === 'Completed').length,
    expired: volunteers.filter(v => v.background_check_status === 'Expired').length
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="background-checks">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Background Check Management
          </h1>
          <p className="text-slate-500 mt-1">Track and update volunteer background check status</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-4 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-xl font-bold text-[#1a3a5c]">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Pending</p>
                  <p className="text-xl font-bold text-amber-600">{stats.pending}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Completed</p>
                  <p className="text-xl font-bold text-emerald-600">{stats.completed}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">Expired</p>
                  <p className="text-xl font-bold text-red-600">{stats.expired}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-slate-400" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="bg-check-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>{status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : volunteers.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Volunteer</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Applied</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {volunteers.map((volunteer) => (
                      <TableRow key={volunteer.volunteer_id} className="table-row-hover">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-[#1a3a5c]">
                                {volunteer.first_name?.[0]}{volunteer.last_name?.[0]}
                              </span>
                            </div>
                            <span className="font-medium text-[#1a3a5c]">
                              {volunteer.first_name} {volunteer.last_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-600">{volunteer.email}</TableCell>
                        <TableCell className="text-slate-600">
                          {volunteer.created_at ? new Date(volunteer.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(volunteer.background_check_status)}</TableCell>
                        <TableCell>
                          <Select 
                            value={volunteer.background_check_status}
                            onValueChange={(value) => updateBackgroundCheck(volunteer.volunteer_id, value)}
                          >
                            <SelectTrigger className="w-32" data-testid={`update-bg-${volunteer.volunteer_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Pending">Pending</SelectItem>
                              <SelectItem value="Completed">Completed</SelectItem>
                              <SelectItem value="Expired">Expired</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Shield className="w-12 h-12 mb-4 text-slate-300" />
                <p>No volunteers found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
